import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import LocationSelector from './LocationSelector';

interface EventRegistrationProps {
  eventId: string;
  onRegistrationChange?: () => void;
}

// Helper functions to get appropriate button text
const getUpdateButtonText = (): string => '📝 Update Registration';
const getRegisterButtonText = (): string => '✅ Register for Event';

const getButtonText = (isRegistered: boolean): string => {
  if (isRegistered) {
    return getUpdateButtonText();
  }
  return getRegisterButtonText();
};

const EventRegistration: React.FC<EventRegistrationProps> = ({ eventId, onRegistrationChange }) => {
  const { user, token } = useAuth();
  const [skills, setSkills] = useState<string>('');
  const [teamPreferences, setTeamPreferences] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [locationOther, setLocationOther] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [isRegistered, setIsRegistered] = useState<boolean>(false);

  // Check if user is already registered for this event
  useEffect(() => {
    const checkRegistration = async () => {
      if (!user?.email || !eventId || !token) return;
      
      try {
        // Use the me/registrations endpoint to check current user's registrations
        const registrations = await apiGet<any[]>('/events/me/registrations', token);
        const currentRegistration = registrations.find((reg: any) => reg.event_id === eventId);
        if (currentRegistration) {
          setIsRegistered(true);
          setSkills(currentRegistration.skills?.join(', ') || '');
          setTeamPreferences(currentRegistration.team_preferences || '');
          setLocation(currentRegistration.location || '');
          setLocationOther(currentRegistration.location_other || '');
        } else {
          setIsRegistered(false);
        }
      } catch (error) {
        console.error('Error checking registration:', error);
        setIsRegistered(false);
      }
    };

    checkRegistration();
  }, [user?.email, eventId, token]);

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  const handleRegistration = async () => {
    if (!token) {
      showMessage('You must be logged in to register', 'error');
      return;
    }

    // Validate location
    if (!location) {
      showMessage('Please select your location', 'error');
      return;
    }

    if (location === 'Other' && !locationOther.trim()) {
      showMessage('Please specify your location when selecting "Other"', 'error');
      return;
    }

    setLoading(true);

    try {
      const skillsArray = skills.split(',').map(skill => skill.trim()).filter(skill => skill.length > 0);
      
      if (isRegistered) {
        await apiPut(`/events/${eventId}/register`, {
          skills: skillsArray,
          team_preferences: teamPreferences,
          location: location,
          location_other: locationOther
        }, token);
        showMessage('Registration updated successfully!', 'success');
      } else {
        await apiPost(`/events/${eventId}/register`, {
          skills: skillsArray,
          team_preferences: teamPreferences,
          location: location,
          location_other: locationOther
        }, token);
        showMessage('Successfully registered for event!', 'success');
        setIsRegistered(true);
      }
      
      if (onRegistrationChange) {
        onRegistrationChange();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to register for event';
      showMessage(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnregister = async () => {
    if (!token || !window.confirm('Are you sure you want to unregister from this event?')) return;

    setLoading(true);

    try {
      await apiDelete(`/events/${eventId}/register`, token);
      showMessage('Successfully unregistered from event', 'success');
      setIsRegistered(false);
      setSkills('');
      setTeamPreferences('');
      setLocation('');
      setLocationOther('');
      if (onRegistrationChange) {
        onRegistrationChange();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to unregister from event';
      showMessage(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <p style={{ margin: '0', color: '#6c757d' }}>
          Please log in to register for this event
        </p>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#f8f9fa', 
      borderRadius: '8px',
      border: '1px solid #dee2e6',
      marginTop: '20px'
    }}>
      <h3 style={{ marginTop: '0', marginBottom: '15px', color: '#333' }}>
        Event Registration
      </h3>

      {message && (
        <div style={{
          padding: '10px',
          marginBottom: '15px',
          borderRadius: '4px',
          backgroundColor: messageType === 'success' ? '#d4edda' : '#f8d7da',
          color: messageType === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${messageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message}
        </div>
      )}

      <div style={{ marginBottom: '15px' }}>
        <label htmlFor="skills-input" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Your Skills for this Event:
        </label>
        <input
          id="skills-input"
          type="text"
          value={skills}
          onChange={(e) => setSkills(e.target.value)}
          placeholder="e.g., Python, React, AWS, Design (comma-separated)"
          style={{ 
            width: '100%', 
            padding: '8px', 
            borderRadius: '4px', 
            border: '1px solid #ccc',
            fontSize: '14px'
          }}
        />
        <small style={{ color: '#666', fontSize: '12px' }}>
          List skills you want to contribute to a team for this event
        </small>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label htmlFor="preferences-input" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Team Preferences (Optional):
        </label>
        <textarea
          id="preferences-input"
          value={teamPreferences}
          onChange={(e) => setTeamPreferences(e.target.value)}
          placeholder="Describe what kind of team or project you're looking for..."
          rows={3}
          style={{ 
            width: '100%', 
            padding: '8px', 
            borderRadius: '4px', 
            border: '1px solid #ccc',
            fontSize: '14px',
            resize: 'vertical'
          }}
        />
        <small style={{ color: '#666', fontSize: '12px' }}>
          Help teams understand what you're interested in working on
        </small>
      </div>

      <LocationSelector
        location={location}
        locationOther={locationOther}
        onLocationChange={setLocation}
        onLocationOtherChange={setLocationOther}
        required={!isRegistered}
        disabled={loading}
      />

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button
          onClick={handleRegistration}
          disabled={loading}
          style={{
            padding: '10px 20px',
            backgroundColor: isRegistered ? '#ffc107' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 'bold',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? '...' : getButtonText(isRegistered)}
        </button>

        {isRegistered && (
          <button
            onClick={handleUnregister}
            disabled={loading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? '...' : '❌ Unregister'}
          </button>
        )}
      </div>

      {isRegistered && (
        <div style={{ 
          marginTop: '15px', 
          padding: '10px', 
          backgroundColor: '#e7f3ff', 
          borderRadius: '4px',
          border: '1px solid #b3d7ff' 
        }}>
          <small style={{ color: '#0056b3', fontSize: '12px' }}>
            ✅ You are registered for this event. You can update your skills and preferences above.
          </small>
        </div>
      )}
    </div>
  );
};

export default EventRegistration;
